import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import MagnumSays from '@/components/MagnumSays'
import ActCommitStep, { CommitState, EMPTY_COMMIT, commitIsReady } from '@/components/act/ActCommitStep'
import ActFullSectionRun from '@/components/act/ActFullSectionRun'
import ActLogList from '@/components/act/ActLogList'
import ActModePicker from '@/components/act/ActModePicker'
import ActSectionPicker from '@/components/act/ActSectionPicker'
import ActStrategyTable from '@/components/act/ActStrategyTable'
import ActSummaryTiles from '@/components/act/ActSummaryTiles'
import ActTimerDisplay from '@/components/act/ActTimerDisplay'
import { useActFlow } from '@/hooks/useActFlow'
import {
  FOCUS_STRATEGY,
  FOCUS_TIME,
  MODE_TEST,
  formatDuration,
  percent,
  verdictLine,
} from '@/lib/actModes'

/**
 * ACT practice: pick a section, pick what this round is testing, then run
 * questions. Everything after that is the same short loop -- call one thing,
 * work it on the clock, right or wrong, next question. The questions themselves
 * live in his prep book; this is the timer and the scorekeeper.
 */
export default function ActPracticePage() {
  const { user } = useAuth()
  const flow = useActFlow()
  const [commit, setCommit] = useState<CommitState>(EMPTY_COMMIT)
  const testOpened = useRef(false)

  useEffect(() => {
    flow.begin()
  }, [])

  // A full-section run opens its first question the moment he starts it --
  // there's nothing to commit to, so nothing to wait for.
  useEffect(() => {
    if (flow.stage === 'run' && flow.mode === MODE_TEST && !testOpened.current) {
      testOpened.current = true
      flow.nextTestQuestion()
    }
    if (flow.stage !== 'run') testOpened.current = false
  }, [flow.stage, flow.mode])

  if (!user || user.type !== 'child') return null

  const { catalog, sectionSpec, focus } = flow

  const handleStart = async () => {
    if (!commitIsReady(focus, commit)) return
    await flow.startQuestion({
      predictedSeconds: focus?.key === FOCUS_TIME ? commit.predictedSeconds : null,
      predictedCorrect: commit.predictedCorrect,
      plannedStrategies: commit.strategy ? [commit.strategy] : [],
    })
  }

  /** Practice: grade and drop straight back onto the next question. */
  const handleGrade = async (correct: boolean) => {
    await flow.grade(correct)
    setCommit(prev => ({ ...prev, predictedCorrect: null }))
  }

  /** Full section: grade, then open the next question on the same tap. */
  const handleTestGrade = async (correct: boolean) => {
    const updated = await flow.grade(correct)
    if (!updated) return
    if (flow.testLogs.length + 1 >= flow.questionsPlanned) {
      await flow.finish()
      return
    }
    await flow.nextTestQuestion()
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6 py-2">
      {/* ---- Pick a section ---- */}
      {flow.stage === 'section' && (
        <>
          <div className="flex items-center justify-between">
            <Link to="/practice" className="text-sm text-muted-foreground hover:text-foreground transition">
              &larr; Back
            </Link>
            <Link
              to="/practice/act/review"
              className="text-sm text-muted-foreground hover:text-foreground transition"
            >
              My numbers &rarr;
            </Link>
          </div>
          {flow.error && <MagnumSays variant="warning" size="sm">{flow.error}</MagnumSays>}
          <ActSectionPicker catalog={catalog} pace={flow.pace} onPick={flow.chooseSection} />
        </>
      )}

      {/* ---- Pick what this round is testing ---- */}
      {flow.stage === 'mode' && sectionSpec && (
        <ActModePicker
          catalog={catalog}
          section={sectionSpec}
          sectionPace={flow.sectionPace}
          paceLevelKey={flow.paceLevelKey || flow.recommendedPaceLevel(flow.section)}
          onPaceLevel={flow.setPaceLevelKey}
          onPractice={flow.startPractice}
          onTest={() => flow.startTest(flow.paceLevelKey || undefined)}
          onBack={flow.restart}
        />
      )}

      {/* ---- Full section against the clock ---- */}
      {flow.stage === 'run' && flow.mode === MODE_TEST && sectionSpec && (
        <>
          {flow.error && <MagnumSays variant="warning" size="sm">{flow.error}</MagnumSays>}
          <ActFullSectionRun
            section={sectionSpec}
            questionBudget={flow.questionBudget}
            runBudget={flow.runBudgetSeconds}
            answered={flow.testLogs.length}
            correct={flow.testLogs.filter(l => l.actualCorrect === true).length}
            questionStartedAt={flow.startedAt}
            runStartedAt={flow.runStartedAt}
            busy={flow.busy}
            onGrade={handleTestGrade}
            onEnd={flow.finish}
          />
        </>
      )}

      {/* ---- One question at a time ---- */}
      {flow.stage === 'run' && flow.mode !== MODE_TEST && focus && (
        <>
          <div className="flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={flow.backToModes}
              className="text-muted-foreground hover:text-foreground transition min-h-[36px]"
            >
              &larr; {sectionSpec?.emoji} {sectionSpec?.label} · {focus.emoji} {focus.label}
            </button>
            <div className="flex items-center gap-3 text-muted-foreground">
              <span className="tabular-nums">
                {flow.correctCount}/{flow.answered} right
              </span>
              {flow.streak >= 2 && <span className="text-amber-500 font-medium">🔥 {flow.streak}</span>}
            </div>
          </div>

          {flow.error && <MagnumSays variant="warning" size="sm">{flow.error}</MagnumSays>}

          {/* Commit */}
          {flow.phase === 'commit' && (
            <div className="space-y-5">
              <MagnumSays>{flow.last ? verdictLine(flow.last) : focus.prompt}</MagnumSays>

              <h1 className="text-lg font-semibold text-center">{focus.question}</h1>

              <ActCommitStep
                focus={focus}
                catalog={catalog}
                section={sectionSpec}
                state={commit}
                onChange={setCommit}
              />

              <div className="flex gap-3">
                <button
                  onClick={handleStart}
                  disabled={flow.busy || !commitIsReady(focus, commit)}
                  className="flex-1 px-6 py-4 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition disabled:opacity-40 min-h-[44px]"
                >
                  Start the clock
                </button>
                {flow.answered > 0 && (
                  <button
                    onClick={flow.finish}
                    className="px-6 py-4 border border-border rounded-xl font-bold hover:bg-accent transition min-h-[44px]"
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Work it */}
          {flow.phase === 'working' && (
            <div className="space-y-6">
              <ActTimerDisplay startedAt={flow.startedAt} hidden />
              <p className="text-sm text-muted-foreground text-center">
                {focus.key === FOCUS_STRATEGY
                  ? 'Lead with the move you picked.'
                  : `You called ${flow.currentLog?.predictedSeconds != null
                      ? formatDuration(flow.currentLog.predictedSeconds)
                      : flow.currentLog?.predictedCorrect ? "that you'd get it" : "that you'd miss it"}.`}
              </p>
              <button
                onClick={flow.stopTimer}
                className="w-full px-6 py-6 bg-primary text-primary-foreground rounded-xl font-bold text-lg hover:opacity-90 transition min-h-[44px]"
              >
                Done — stop the clock
              </button>
            </div>
          )}

          {/* Right or wrong, then straight back to the next one */}
          {flow.phase === 'grade' && (
            <div className="space-y-6">
              <ActTimerDisplay startedAt={null} frozenSeconds={flow.elapsedSeconds} />
              <p className="text-sm text-muted-foreground text-center">Check the key — did you get it?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleGrade(true)}
                  disabled={flow.busy}
                  className="px-4 py-6 rounded-xl border-2 border-green-500 bg-green-50 text-green-800 font-bold text-lg hover:bg-green-100 transition disabled:opacity-40 min-h-[44px]"
                >
                  Right
                </button>
                <button
                  onClick={() => handleGrade(false)}
                  disabled={flow.busy}
                  className="px-4 py-6 rounded-xl border-2 border-red-400 bg-red-50 text-red-800 font-bold text-lg hover:bg-red-100 transition disabled:opacity-40 min-h-[44px]"
                >
                  Wrong
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ---- How the round went ---- */}
      {flow.stage === 'done' && (
        <div className="space-y-6">
          <MagnumSays variant="success">
            {flow.summary && flow.summary.questionsCompleted > 0
              ? `${flow.summary.questionsCompleted} questions, ${percent(flow.summary.accuracy)} right, ${formatDuration(flow.summary.avgActualSeconds)} apiece. That's the whole story.`
              : "Nothing logged this round. Come back when you've got a book open."}
          </MagnumSays>

          {flow.summary && <ActSummaryTiles summary={flow.summary} />}

          {flow.strategyStats.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Moves you led with</h2>
              <ActStrategyTable strategies={flow.strategyStats} />
            </div>
          )}

          <div>
            <h2 className="text-lg font-semibold mb-2">Every question</h2>
            <ActLogList logs={flow.logs} catalog={catalog} />
          </div>

          <div className="flex gap-3 flex-wrap">
            <Link
              to="/practice/act/review"
              className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center min-h-[44px]"
            >
              My numbers
            </Link>
            <Link
              to="/practice"
              className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center min-h-[44px]"
            >
              Back to exercises
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
